import { useSuspenseQuery } from "@apollo/client"
import { Box, Button, Flex, Text } from "@singita/components"
import { graphql } from "gatsby"
import React, { useEffect, useRef, useState } from "react"
import { Translation } from "react-i18next"
import { client } from "../../apollo/availability-client"
import { FORM_STATUS } from "./common/constants"
import { FormConfirmation } from "./components/FormConfirmation"
import FormLoading from "./components/FormLoading"
import FormTitle from "./components/FormTitle"
import FormBuilder from "./FormBuilder"
import { FORM_QUERY } from "./graphql/queries"
import { useSubmitForm } from "./hooks/useSubmitForm"
import { getIpAddress } from "./services/ipAddress"
import { uploadToS3 } from "./services/s3"
import { fileTypesMap, normalizeFields, validate } from "./utils/helpers"
import { useCampaignTracker } from "gatsby-singita-theme/src/hooks"
import { addCampaignDataToFields } from "gatsby-singita-theme/src/utils/campaignUtils"

const DEFAULT_FORM_VALUES = { newsletter: false }

export const FormWrapper = ({
  successMessage,
  styles,
  formId,
  zapierUrl,
  events = [],
  defaultValues = null,
  mergeFields = {},
  ...rest
}) => {
  const [form, setForm] = useState({ ...defaultValues, ...DEFAULT_FORM_VALUES })
  const { campaignData = {} } = useCampaignTracker()
  const [errors, setErrors] = useState({})
  const formRef = useRef()
  const confirmRef = useRef()
  const [submitForm, apiLoading, status, changeStatus] = useSubmitForm(formId)
  const [uploadError, setUploadError] = useState(false)
  const { loading, error, data } = useSuspenseQuery(FORM_QUERY, {
    variables: {
      id: formId || "enquiry",
    },
    client,
  })
  const [submitLoader, setSubmitLoader] = useState(false)
  const [hideForm, setHideForm] = useState(false)

  useEffect(() => {
    if (status === FORM_STATUS.SUCCESS) {
      resetForm()
      setHideForm(true)
      confirmRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }

    if (status === FORM_STATUS.FAILED) {
      setSubmitLoader(false)
      confirmRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [status, error])

  const handleSubmit = async extra => {
    //e.preventDefault()
    setErrors({})
    if (form.a_password) {
      return null
    }
    setSubmitLoader(true)
    changeStatus(FORM_STATUS.PENDING)

    const errors = await validate(fields, form)

    if (Object.keys(errors).length || uploadError) {
      setSubmitLoader(false)
      setErrors(errors)
      return null
    } else {
      const promises = []
      const files = []

      Object.keys(form).forEach(field => {
        if (fileTypesMap.includes(form[field].type)) {
          promises.push(uploadToS3(form[field]))
        }
      })

      if (promises.length) {
        await Promise.all(promises)
          .then(values => {
            files.push(...values)
          })
          .catch(err => {
            console.log("upload error", err)
            setUploadError(true)
          })
      }

      const ip = await getIpAddress()
      const url = typeof window !== "undefined" ? window.location.href : ""

      const recaptcha = {
        recaptchaToken: extra.recaptchaToken || "",
        recaptchaAction: extra.recaptchaAction || "",
        recaptchaV2Token: extra.recaptchaV2Token || "",
      }
      try {
        const data = await submitForm({
          variables: {
            input: {
              form: formId,
              fields: {
                ...normalizeFields(form),
                files: files,
                path: url,
                ip_address: ip,
                zapierUrl: zapierUrl,
                newsletterListId: rest.newsletterListId,
                campaignData: addCampaignDataToFields(campaignData),
              },
              extra: { ...rest },
              ...recaptcha,
            },
          },
        })

        const response = data?.data?.submitTransactionWebsiteForm

        if (!response) {
          throw new Error("Failed to submit form")
        }

        // Handle challenge requirement
        if (response.requiresChallenge && !extra.recaptchaV2Token) {
          // Don't show error, just return the response to trigger v2
          setSubmitLoader(false)
          changeStatus(FORM_STATUS.PENDING)
          return response
        }

        // Check if verification succeeded
        if (!response.recaptchaSuccess) {
          setErrors({
            ...errors,
            recaptcha: "Verification failed. Please try again.",
          })
          changeStatus(FORM_STATUS.FAILED)
          setSubmitLoader(false)
          return null
        }

        // Success - mark as successful
        changeStatus(FORM_STATUS.SUCCESS)

        return response
      } catch (error) {
        console.error("Form submission error:", error)
        changeStatus(FORM_STATUS.FAILED)
        setSubmitLoader(false)
      }
    }
  }

  const resetForm = () => {
    setSubmitLoader(false)
    setErrors({})
    setForm(DEFAULT_FORM_VALUES)
    formRef.current.reset()
  }

  const handleInputChange = (event, inputProps) => {
    if (inputProps) {
      setForm({
        ...form,
        [inputProps.name]: inputProps.value,
      })
    } else {
      const target = event.target
      const value = target.type === "checkbox" ? target.checked : target.value
      const name = target.name

      setForm({
        ...form,
        [name]: name === "email" ? value.toLowerCase() : value,
      })
    }
  }

  if (loading) {
    return <FormLoading />
  }

  if (!data?.viewForm) {
    return `No form found for formId: ${formId}`
  }

  if (error) {
    return (
      <FormConfirmation
        response={FORM_STATUS.FAILED}
        message={"Error fetching form."}
      />
    )
  }

  const { buttonText = "", fields = [] } = data?.viewForm

  const merged = [...fields]
  if (mergeFields) {
    Object.keys(mergeFields).map(f => {
      const index = merged.findIndex(m => m.fieldId === f)
      merged[index] = { ...merged[index], ...mergeFields[f] }
    })
  }

  const isLoading = submitLoader || apiLoading

  return (
    <>
      <Box ref={confirmRef}>
        <FormConfirmation
          response={status}
          message={successMessage}
          uploadError={uploadError}
          reference={confirmRef}
        >
          {hideForm ? (
            <Button
              onClick={() => {
                changeStatus(FORM_STATUS.PENDING)
                setHideForm(false)
              }}
            >
              <Translation>{t => t("form.show-form")}</Translation>
            </Button>
          ) : null}
        </FormConfirmation>
      </Box>
      <Box mb={[2]} hidden={hideForm}>
        <FormBuilder
          reference={formRef}
          formId={formId}
          fields={merged}
          onSubmit={handleSubmit}
          onChange={handleInputChange}
          errors={errors}
          formStyles={styles}
          buttonText={
            buttonText || <Translation>{t => t("form.button")}</Translation>
          }
          renderTitle={() => <FormTitle title={rest.title} />}
          form={form}
          setForm={setForm}
          isLoading={isLoading}
        />
      </Box>
    </>
  )
}

const SuspendedFormWrapper = props => (
  <React.Suspense fallback={<FormLoading />}>
    <FormWrapper {...props} />
  </React.Suspense>
)
export default SuspendedFormWrapper

export const formFields = graphql`
  fragment FormFields on ContentfulForm {
    id
    contentful_id
    formId
    newsletterListId
    successMessage
    title
    desc: description
    zapierUrl
    node_locale
    events {
      type
      category
      action
    }
    styles {
      flexDirection
      alignItems
      justifyContent
      mb
      flexWrap
    }
  }
`
